/**
 * Session Timeout Hook
 * 
 * Monitors user activity and automatically logs out after a period of inactivity.
 * This enhances security by preventing unauthorized access to unattended sessions.
 * 
 * SECURITY NOTE: This is a client-side feature for UX. Server-side session
 * management should also be configured for proper security.
 */

import { useEffect, useRef, useCallback, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

// Timeout durations in milliseconds
const TIMEOUT_DURATIONS = {
  "15m": 15 * 60 * 1000,
  "30m": 30 * 60 * 1000,
  "1h": 60 * 60 * 1000,
  "4h": 4 * 60 * 60 * 1000,
  "24h": 24 * 60 * 60 * 1000,
} as const;

export type TimeoutDuration = keyof typeof TIMEOUT_DURATIONS;

const WARNING_BEFORE_TIMEOUT = 60 * 1000; // 1 minute warning

// Events that indicate user activity
const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "click",
  "focus",
];

interface UseSessionTimeoutOptions {
  timeout?: TimeoutDuration;
  onWarning?: () => void;
  onTimeout?: () => void;
  enabled?: boolean;
}

interface SessionTimeoutState {
  isWarningShown: boolean;
  remainingTime: number;
  isActive: boolean;
}

export function useSessionTimeout({
  timeout = "4h",
  onWarning,
  onTimeout,
  enabled = true,
}: UseSessionTimeoutOptions = {}) {
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  
  const [state, setState] = useState<SessionTimeoutState>({
    isWarningShown: false,
    remainingTime: TIMEOUT_DURATIONS[timeout],
    isActive: true,
  });
  
  const lastActivityRef = useRef<number>(Date.now());
  const warningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  const timeoutDuration = TIMEOUT_DURATIONS[timeout];
  
  // Clear all timeouts
  const clearAllTimeouts = useCallback(() => {
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
      logoutTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);
  
  // Handle session timeout - logout user
  const handleTimeout = useCallback(async () => {
    clearAllTimeouts();
    
    if (onTimeout) {
      onTimeout();
    } else {
      toast({
        title: "Session Expired",
        description: "You have been logged out due to inactivity.",
        variant: "destructive",
      });
    }
    
    await signOut();
  }, [signOut, toast, onTimeout, clearAllTimeouts]);
  
  // Show warning before timeout
  const showWarning = useCallback(() => {
    setState(prev => ({ ...prev, isWarningShown: true }));
    
    if (onWarning) {
      onWarning();
    } else {
      toast({
        title: "Session Expiring Soon",
        description: "Your session will expire in 1 minute due to inactivity. Move your mouse or press a key to stay logged in.",
        duration: 10000,
      });
    }
    
    // Start countdown
    countdownIntervalRef.current = setInterval(() => {
      const remaining = lastActivityRef.current + timeoutDuration - Date.now();
      setState(prev => ({ ...prev, remainingTime: Math.max(0, remaining) }));
      
      if (remaining <= 0) {
        handleTimeout();
      }
    }, 1000);
    
    // Schedule logout
    logoutTimeoutRef.current = setTimeout(handleTimeout, WARNING_BEFORE_TIMEOUT);
  }, [onWarning, toast, timeoutDuration, handleTimeout]);
  
  // Reset activity timer - use ref to avoid dependency issues
  const resetTimerRef = useRef<() => void>(() => {});
  
  resetTimerRef.current = () => {
    lastActivityRef.current = Date.now();
    
    // Clear existing timeouts
    clearAllTimeouts();
    
    // Reset state
    setState({
      isWarningShown: false,
      remainingTime: timeoutDuration,
      isActive: true,
    });
    
    // Only set new timeouts if enabled and user is logged in
    if (!enabled || !user) return;
    
    // Set warning timeout
    const warningTime = timeoutDuration - WARNING_BEFORE_TIMEOUT;
    if (warningTime > 0) {
      warningTimeoutRef.current = setTimeout(showWarning, warningTime);
    }
  };
  
  const resetTimer = useCallback(() => {
    resetTimerRef.current();
  }, []);
  
  // Throttled activity handler to prevent excessive calls
  const activityHandler = useCallback(() => {
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;
    
    // Only reset if more than 1 second since last activity to prevent spam
    if (timeSinceLastActivity > 1000) {
      resetTimerRef.current();
    }
  }, []);
  
  // Set up activity listeners - only re-run when user or enabled changes
  useEffect(() => {
    if (!enabled || !user) {
      clearAllTimeouts();
      return;
    }
    
    // Initial timer setup
    resetTimerRef.current();
    
    // Add activity listeners
    const handler = () => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivityRef.current;
      if (timeSinceLastActivity > 1000) {
        resetTimerRef.current();
      }
    };
    
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handler, { passive: true });
    });
    
    // Handle visibility change (user returns to tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Check if session should have expired while tab was inactive
        const timeSinceLastActivity = Date.now() - lastActivityRef.current;
        if (timeSinceLastActivity >= timeoutDuration) {
          handleTimeout();
        } else {
          handler();
        }
      }
    };
    
    document.addEventListener("visibilitychange", handleVisibilityChange);
    
    return () => {
      clearAllTimeouts();
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handler);
      });
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, user?.id, timeoutDuration, handleTimeout, clearAllTimeouts]);
  
  // Extend session manually
  const extendSession = useCallback(() => {
    resetTimer();
    toast({
      title: "Session Extended",
      description: "Your session has been extended.",
    });
  }, [resetTimer, toast]);
  
  return {
    isWarningShown: state.isWarningShown,
    remainingTime: state.remainingTime,
    remainingMinutes: Math.ceil(state.remainingTime / 60000),
    remainingSeconds: Math.ceil(state.remainingTime / 1000) % 60,
    extendSession,
    resetTimer,
  };
}

export { TIMEOUT_DURATIONS };
export default useSessionTimeout;
